import React, { useState } from 'react';
import { Plus, Edit2, Unlock, Power } from 'lucide-react';
import usersService from '../services/users';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import RoleChecklist from '../components/common/RoleChecklist';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { NONE, STATUS_OPTIONS, formatDate } from '../components/common/filters';
import useServerTable from '../hooks/useServerTable';
import useLegacyQueryParams from '../hooks/useLegacyQueryParams';
import useRoles from '../hooks/useRoles';
import useTenants from '../hooks/useTenants';
import { splitRoles, mergeRoleIds } from '../utils/roleFlags';
import { getToken } from '../utils/storage';
import { decodeJwtPayload } from '../utils/jwt';
import { Field } from '../components/ui/Field';
import { TextInput, Select } from '../components/ui/Input';

const parseConfig = (cfg) => {
    if (!cfg) return {};
    if (typeof cfg === 'object') return cfg;
    try {
        return JSON.parse(cfg) || {};
    } catch {
        return {};
    }
};

const singleTenant = (values) => {
    const t = values?.tenant;
    return Array.isArray(t) && t.length === 1 && t[0] !== NONE ? t[0] : '';
};

const UsersPage = () => {
    const { loading: loadingApps, error: appsError, selectedAppId: appId, selectedApp: currentApp } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    // Older links used ?role_id= (Roles list "Users" count) and ?tenant_id= (Tenants page).
    useLegacyQueryParams({ role_id: 'role', tenant_id: 'tenant' });
    // Tenant of the user being edited; kept apart so editing never re-scopes the list.
    const [editTenantId, setEditTenantId] = useState('');
    // Roles and tenants belong to the selected app.
    const rolesQuery = useRoles(appId);
    const tenantsQuery = useTenants(appId);
    const roles = rolesQuery.items;
    const tenants = tenantsQuery.items;
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        first_name: '',
        last_name: '',
        gender: '',
        email_id: '',
        phone_no: '',
        role_ids: [],
    });

    const isMultiTenant = currentApp?.is_multi_tenant === 1 || currentApp?.is_multi_tenant === '1';
    // AIQ-438: a portal admin picks the new user's tenant (or App-level) for a multi-tenant app;
    // a tenant admin always creates in their own tenant (the server enforces it). The token is
    // read for display only — never for authorisation.
    const ownTenantId = decodeJwtPayload(getToken())?.tenant_id || '';
    const showTenantPicker = isMultiTenant && !ownTenantId;
    // '' = not chosen yet, NONE = App-level, otherwise a tenant_id.
    const [createTenant, setCreateTenant] = useState('');
    const createTenantId = showTenantPicker ? (createTenant === NONE ? '' : createTenant) : ownTenantId;
    // The form's roles follow its tenant: application roles plus that tenant's own; an
    // App-level user gets application roles only (the server refuses another tenant's role).
    const createRolesQuery = useRoles(appId, createTenantId || null, { enabled: showCreateModal });
    const createRoles = createTenantId ? createRolesQuery.items : createRolesQuery.items.filter((r) => !r.tenant_id);
    const totpAvailable = Boolean(parseConfig(currentApp?.app_config).totp_mfa_enabled);

    // URL keys: status, tenant, role, created_from/created_to (one per column), plus two presets.
    const columns = [
        {
            key: 'username',
            header: 'User',
            sortKey: 'username',
            locked: true,
            render: (u) => (
                <div className="min-w-0">
                    <div className="font-medium text-slate-900">{u.username}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email_id}</div>
                </div>
            ),
        },
        {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—',
        },
        ...(isMultiTenant
            ? [
                  {
                      key: 'tenant',
                      header: 'Tenant',
                      sortKey: 'tenant',
                      filter: {
                          type: 'multiselect',
                          defaultExpanded: true,
                          options: [
                              { value: NONE, label: 'App-level' },
                              ...tenants.map((t) => ({ value: t.tenant_id, label: t.tenant_name || t.tenant_id })),
                          ],
                      },
                      render: (u) =>
                          u.tenant_id ? (
                              tenantsQuery.byId[u.tenant_id]?.tenant_name || (
                                  <span className="font-mono text-xs">{u.tenant_id}</span>
                              )
                          ) : (
                              <span className="text-slate-500">App-level</span>
                          ),
                  },
              ]
            : []),
        {
            key: 'roles',
            header: 'Roles',
            filter: {
                key: 'role',
                type: 'multiselect',
                options: roles
                    .filter((r) => r.presentation !== 'FLAG')
                    .map((r) => ({ value: r.role_id, label: r.role_name })),
                emptyText: 'This app has no roles yet.',
            },
            render: (u) => {
                const { roles: plain, flags } = roleNames(u);
                return (
                    <div className="max-w-xs">
                        <div className="truncate" title={plain}>
                            {plain || '—'}
                        </div>
                        {flags && <div className="text-xs text-slate-500 truncate">flags: {flags}</div>}
                    </div>
                );
            },
        },
        { key: 'phone_no', header: 'Phone', render: (u) => u.phone_no || '—' },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (u) => <StatusBadge active={Boolean(u.is_user_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        { key: 'gender', header: 'Gender', defaultVisible: false, render: (u) => u.gender || '—' },
        {
            key: 'created',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (u) => (u.created_on ? formatDate(u.created_on) : '—'),
            filter: { type: 'date' },
        },
        {
            key: 'modified_on',
            header: 'Last modified',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (u) => (u.modified_on ? formatDate(u.modified_on) : '—'),
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (u) => (
                <RowActions>
                    <RowAction icon={Edit2} label="Edit user" onClick={() => openUpdateModal(u)} />
                    <RowAction
                        icon={Power}
                        label={u.is_user_active ? 'Deactivate user' : 'Activate user'}
                        tone={u.is_user_active ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(u.username, u.is_user_active)}
                    />
                    <RowAction
                        icon={Unlock}
                        label="Unlock account"
                        tone="info"
                        onClick={() => handleUnlock(u.username)}
                    />
                </RowActions>
            ),
        },
    ];

    const table = useServerTable(
        (query, values) => usersService.getPage(appId.trim(), query, singleTenant(values) || null),
        {
            deps: [appId],
            enabled: Boolean(appId && appId.trim()),
            defaultSort: { key: 'created', dir: 'desc' },
            columns,
            // Account flags the rows do not carry: drill-down links only (chip, no column).
            presetFields: [
                { key: 'locked', label: 'Sign-in lockout', type: 'boolean', trueLabel: 'Locked', falseLabel: 'Not locked' },
                ...(totpAvailable
                    ? [
                          {
                              key: 'mfa',
                              label: 'Authenticator app',
                              type: 'boolean',
                              trueLabel: 'Set up',
                              falseLabel: 'Not set up',
                          },
                      ]
                    : []),
            ],
            scopeKey: appId,
            scopedFilters: ['tenant', 'role'],
        }
    );
    // Exactly one real tenant filtered = the list is scoped to it (as the old ?tenant_id= link
    // was); the Create User form starts on that tenant.
    const tenantId = singleTenant(table.filters);

    // Role picks ∪ flags (flags dropped for an admin account); the backend full-replaces.
    // `list` = the roles offered by the form being submitted.
    const submittedRoleIds = (list = roles) => {
        // Ids not in the loaded list are kept as roles (never silently dropped).
        const { flags } = splitRoles(list);
        const isFlag = (id) => flags.some((r) => r.role_id === id);
        const ids = formData.role_ids;
        return mergeRoleIds(
            ids.filter((id) => !isFlag(id)),
            ids.filter(isFlag),
            list
        );
    };
    const hasPlainRole = (list = roles) => splitRoles(list).roles.some((r) => formData.role_ids.includes(r.role_id));
    const tenantMissing = showTenantPicker && !createTenant;

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        setError('');
        try {
            await usersService.createUser(
                appId,
                { ...formData, role_ids: submittedRoleIds(createRoles) },
                showTenantPicker ? createTenantId || null : null
            );
            setShowCreateModal(false);
            resetForm();
            table.reload();
            notice.success('User created successfully!');
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create user');
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        if (!selectedUser) return;
        if (!hasPlainRole()) {
            setError('Select at least one role');
            return;
        }
        setError('');
        try {
            await usersService.updateDetails(
                appId,
                { ...formData, username: selectedUser.username, role_ids: submittedRoleIds() },
                editTenantId || tenantId || null
            );
            setShowUpdateModal(false);
            setSelectedUser(null);
            resetForm();
            table.reload();
            notice.success('User updated successfully!');
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update user');
        }
    };

    const handleToggleStatus = async (username, currentStatus) => {
        const verb = currentStatus ? 'Deactivate' : 'Activate';
        const ok = await confirm({
            title: `${verb} user ${username}?`,
            body: currentStatus
                ? 'They can’t sign in to any app until the account is activated again.'
                : 'They can sign in again with their existing roles.',
            confirmLabel: verb,
            tone: currentStatus ? 'danger' : 'default',
        });
        if (!ok) return;
        try {
            await usersService.updateStatus(username, currentStatus ? 0 : 1);
            table.reload();
            notice.success(`User ${username} ${currentStatus ? 'deactivated' : 'activated'}.`);
        } catch (err) {
            notice.error(err, `Couldn’t ${currentStatus ? 'deactivate' : 'activate'} ${username}. Try again.`);
        }
    };

    const handleUnlock = async (username) => {
        try {
            await usersService.unlockAccount(username);
            table.reload();
            notice.success(`Account ${username} unlocked.`);
        } catch (err) {
            notice.error(err, `Couldn’t unlock ${username}. Try again.`);
        }
    };

    const resetForm = () => {
        setFormData({
            username: '',
            first_name: '',
            last_name: '',
            gender: '',
            email_id: '',
            phone_no: '',
            role_ids: [],
        });
    };

    const openUpdateModal = (user) => {
        setError('');
        const userRoleIds = Array.isArray(user.roles) ? user.roles.map((r) => r.role_id).filter(Boolean) : [];
        setSelectedUser(user);
        // The user's own tenant is the context for the update call
        setEditTenantId(user.tenant_id || '');
        setFormData({
            username: user.username || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email_id: user.email_id || '',
            phone_no: user.phone_no || '',
            gender: user.gender || '',
            role_ids: userRoleIds,
        });
        setShowUpdateModal(true);
    };

    // A user's roles and flags as display strings (flags are never shown as roles).
    const roleNames = (u) => {
        const { roles: plain, flags } = splitRoles(Array.isArray(u.roles) ? u.roles : []);
        const names = (list) => list.map((r) => r.role_name).join(', ');
        return { roles: names(plain), flags: names(flags) };
    };


    return (
        <div className="space-y-4">
            <PageHeader
                title="Users"
                actions={
                    appId && (
                        <Button
                            onClick={() => {
                                resetForm();
                                setError('');
                                setCreateTenant(tenantId);
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" /> Add User
                        </Button>
                    )
                }
            />

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its users.</p>
                </div>
            ) : (
                <>
                    {(rolesQuery.error || tenantsQuery.error) && (
                        <div className="mb-4">
                            <ErrorState message={rolesQuery.error || tenantsQuery.error} />
                        </div>
                    )}
                    <DataTable
                        {...table.tableProps}
                        columns={columns}
                        tableKey="users"
                        rowKey={(u) => `${u.username}:${u.tenant_id || ''}`}
                        searchPlaceholder="Search name, email, phone, role…"
                        emptyMessage="No users found for the selected app."
                    />
                </>
            )}

            {/* Create User Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New User" size="lg">
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Username *">
                            <TextInput
                                type="text"
                                required
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                            />
                        </Field>
                        <Field label="Email *">
                            <TextInput
                                type="email"
                                required
                                value={formData.email_id}
                                onChange={(e) => setFormData({ ...formData, email_id: e.target.value.toLowerCase() })}
                            />
                        </Field>
                        <Field label="First Name *">
                            <TextInput
                                type="text"
                                required
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            />
                        </Field>
                        <Field label="Last Name *">
                            <TextInput
                                type="text"
                                required
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            />
                        </Field>
                        <Field label="Gender *">
                            <Select
                                required
                                value={formData.gender}
                                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            >
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </Select>
                        </Field>
                        <Field label="Phone *">
                            <TextInput
                                type="tel"
                                required
                                value={formData.phone_no}
                                onChange={(e) => setFormData({ ...formData, phone_no: e.target.value })}
                            />
                        </Field>
                    </div>
                    {showTenantPicker && (
                        <Field label="Tenant *">
                            <Select
                                required
                                value={createTenant}
                                // Roles depend on the tenant: a new tenant starts a new role pick.
                                onChange={(e) => {
                                    setCreateTenant(e.target.value);
                                    setFormData((prev) => ({ ...prev, role_ids: [] }));
                                }}
                            >
                                <option value="">Select a tenant</option>
                                <option value={NONE}>App-level (no tenant)</option>
                                {tenants.map((t) => (
                                    <option key={t.tenant_id} value={t.tenant_id}>
                                        {t.tenant_name || t.tenant_id}
                                    </option>
                                ))}
                            </Select>
                        </Field>
                    )}
                    <RoleChecklist
                        roles={createRoles}
                        selected={formData.role_ids}
                        onChange={(ids) => setFormData((prev) => ({ ...prev, role_ids: ids }))}
                    />
                    <p className="text-xs text-slate-500">Password will be auto-generated and sent via email.</p>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            disabled={!hasPlainRole(createRoles) || tenantMissing}
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700 disabled:opacity-50"
                        >
                            Create User
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Update User Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => setShowUpdateModal(false)}
                title={`Update ${selectedUser?.username || 'User'}`}
                size="md"
            >
                <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                            <input
                                type="text"
                                value={formData.username}
                                disabled
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={formData.email_id}
                                disabled
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50"
                            />
                        </div>
                        <Field label="First Name">
                            <TextInput
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            />
                        </Field>
                        <Field label="Gender">
                            <Select
                                value={formData.gender}
                                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            >
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </Select>
                        </Field>
                        <Field label="Last Name">
                            <TextInput
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            />
                        </Field>
                        <Field label="Phone">
                            <TextInput
                                type="tel"
                                value={formData.phone_no}
                                onChange={(e) => setFormData({ ...formData, phone_no: e.target.value })}
                            />
                        </Field>
                    </div>

                    <RoleChecklist
                        roles={roles}
                        selected={formData.role_ids}
                        onChange={(ids) => setFormData((prev) => ({ ...prev, role_ids: ids }))}
                    />

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update User
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowUpdateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default UsersPage;
